import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import { getNumberInputDisplayValue, parseNumberInputValue } from "../../../utils/numberInputUtils";
import PartDisplay from '../../Common/PartDisplay';
import {
    Typography,
    Box,
    Paper,
    CircularProgress,
    Chip,
    Divider,
    TextField,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tab,
    Tabs,
    Grid,
    Card,
    CardContent,
    CardHeader,
    Button
} from '@material-ui/core';

import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import GetAppIcon from '@material-ui/icons/GetApp';
import DrawerMenuRight from '../../Common/DrawerMenuRight';
import { ServiceLocator } from '../../../Services/ServiceLocator';
import { v4 as uuid } from 'uuid';
import { fromUtcForDisplay, formatUtcForDisplay } from '../../../utils/dateUtils';
import SortableTableHeader from '../../Common/SortableTableHeader';
import { useTableSort } from '../../../hooks/useTableSort';
import { RECEIVING_ACTIONS_SORT_ACCESSORS } from '../../../utils/tableSortUtils';
import { isPackingSlipFullyReceived, withShipmentFile, parseMultiBoxName } from '../../../utils/packingSlipShipmentUtils';

import { AuthenticationService } from "../../../Services/AuthenticationService";
const styles = theme => ({
    detailsContainer: {
        padding: 16,
        marginBottom: 24,
    },
    sectionTitle: {
        marginBottom: 16,
        fontWeight: 'bold',
    },
    packingSlipInfo: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: 16,
        marginBottom: 16,
    },
    infoItem: {
        display: 'flex',
        flexDirection: 'column',
    },
    label: {
        fontWeight: 'bold',
        color: theme.palette?.text?.secondary || '#666',
        fontSize: '0.875rem',
    },
    value: {
        marginTop: 4,
    },
    receivingActionsTable: {
        marginTop: 24,
    },
    editingRow: {
        backgroundColor: theme.palette?.action?.hover || '#f5f5f5',
    },
    statusChip: {
        fontWeight: 'bold',
        fontSize: '14px',
        textTransform: 'uppercase',
    },
    headerRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 20,
    },
    vendorInfo: {
        flex: 1,
    },
    statusContainer: {
        display: 'flex',
        justifyContent: 'center',
        flex: 1,
    },
    spacer: {
        flex: 1,
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: 200,
    },
    actionButtons: {
        marginBottom: 20,
        display: 'flex',
        justifyContent: 'space-between',
        gap: 10,
    },
    leftButtons: {
        display: 'flex',
        gap: 10,
    },
    rightButtons: {
        display: 'flex',
        gap: 10,
    },
    itemsTable: {
        marginBottom: 30,
    }
});

function DetailsShell({ asPage, open, onClose, title, hasUnsavedChanges, onSubmit, submitConfirmMessage, children }) {
    if (asPage) {
        return <div>{children}</div>;
    }
    return (
        <DrawerMenuRight
            open={open}
            onClose={onClose}
            title={title}
            hasUnsavedChanges={hasUnsavedChanges}
            customWidth={1100}
            onSubmit={onSubmit}
            submitConfirmMessage={submitConfirmMessage}
        >
            {children}
        </DrawerMenuRight>
    );
}

const PackingSlipDetails = ({ open, onClose, packingSlipId, onUpdate, parts, partFamilies, packingSlips, classes, asPage, onRequestEdit, onRequestDuplicate }) => {
    const [packingSlip, setPackingSlip] = useState(null);
    const [receivingActions, setReceivingActions] = useState([]);
    const [editingActionId, setEditingActionId] = useState(null);
    const [editingAction, setEditingAction] = useState({});
    const [loading, setLoading] = useState(false);
    const [customReceivingMode, setCustomReceivingMode] = useState(false);
    const [receiveQuantities, setReceiveQuantities] = useState({});
    const [receivingPackingSlip, setReceivingPackingSlip] = useState(false);
    const [deletingPackingSlip, setDeletingPackingSlip] = useState(false);
    const [editingItemMode, setEditingItemMode] = useState(false);
    const [availablePurchasedParts, setAvailablePurchasedParts] = useState([]);
    const [allPurchasedParts, setAllPurchasedParts] = useState([]);
    const [addingNewItem, setAddingNewItem] = useState(false);
    const [newItem, setNewItem] = useState({ purchasedPartId: '', quantityExpected: 1 });
    const [editedQuantities, setEditedQuantities] = useState({});
    const [savingChanges, setSavingChanges] = useState(false);
    
    // Edit mode state
    const [editingPackingSlip, setEditingPackingSlip] = useState(false);
    const [editedPackingSlip, setEditedPackingSlip] = useState({});
    const [uploadingFile, setUploadingFile] = useState(false);

    const {
        sortField: receivingActionsSortField,
        sortDirection: receivingActionsSortDirection,
        handleSort: handleReceivingActionsSort,
        sortItems: sortReceivingActions,
    } = useTableSort('receivedDate', 'desc', RECEIVING_ACTIONS_SORT_ACCESSORS);

    const sortedReceivingActions = sortReceivingActions(receivingActions);

    useEffect(() => {
        if ((open || asPage) && packingSlipId) {
            loadData();
        }
    }, [open, packingSlipId]);

    const loadData = async () => {
        setLoading(true);
        try {
            const partService = ServiceLocator.Instance.getPartService();
            
            // Load packing slip details
            let packingSlipData = await partService.getPurchaseOrderPackingSlipById(packingSlipId);
            packingSlipData = withShipmentFile(packingSlipData, packingSlips);

            // Multi-box shipments store the file on box 1. If this box has none, look at the rest of the shipment.
            if (packingSlipData && !(packingSlipData.fileUrl || packingSlipData.FileUrl)) {
                try {
                    const shipmentSlips = await partService.getAllPackingSlips();
                    packingSlipData = withShipmentFile(packingSlipData, shipmentSlips);
                } catch (fileResolveError) {
                    console.error('Error resolving packing slip file from shipment:', fileResolveError);
                }
            }

            setPackingSlip(packingSlipData);
            
            // Initialize receive quantities with expected quantities
            if (packingSlipData?.items) {
                const quantities = {};
                packingSlipData.items.forEach(item => {
                    quantities[item.id] = item.quantityExpected;
                });
                setReceiveQuantities(quantities);
            }
            
            // Load receiving actions for audit trail
            const actionsData = await partService.getReceivingActionsByPackingSlipId(packingSlipId);
            setReceivingActions(actionsData);

            // Load all purchased parts for the same vendor (for part info lookup)
            if (packingSlipData && packingSlipData.vendorName) {
                const allParts = await partService.getAllPurchasedPartsByVendor(packingSlipData.vendorName);
                console.log('Loaded all purchased parts for vendor:', packingSlipData.vendorName, allParts);
                setAllPurchasedParts(allParts);
                
                // Filter to show parts that have remaining available quantity (for Add Item dropdown)
                const filteredParts = allParts.filter(part => {
                    const remainingAvailable = part.quantityOrdered - part.quantityReceived;
                    
                    // Show if there's any remaining quantity available
                    return remainingAvailable > 0;
                });
                
                setAvailablePurchasedParts(filteredParts);
            }
        } catch (error) {
            console.error('Error loading packing slip details:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleReceiveAll = async () => {
        setReceivingPackingSlip(true);
        try {
            const partService = ServiceLocator.Instance.getPartService();
            const userId = AuthenticationService.getInstance().getUserId();
            
            // For partially received packing slips, calculate outstanding quantities
            // and use the custom receive functionality
            if (packingSlip.hasAnyReceiving) {
                const outstandingQuantities = {};
                if (packingSlip.items && packingSlip.items.length > 0) {
                    packingSlip.items.forEach(item => {
                        const outstanding = Math.max(0, item.quantityExpected - item.quantityReceived);
                        if (outstanding > 0) {
                            outstandingQuantities[item.id] = outstanding;
                        }
                    });
                }
                
                await partService.receivePackingSlipWithQuantities(packingSlipId, outstandingQuantities, userId);
            } else {
                // For non-received packing slips, use the original method
                await partService.receivePurchaseOrderPackingSlip(packingSlipId, userId);
            }
            
            await loadData();
            if (onUpdate) onUpdate();
            window.$alertSuccess('Packing slip received');
        } catch (error) {
            console.error('Error receiving packing slip:', error);
            alert('Error receiving packing slip');
        } finally {
            setReceivingPackingSlip(false);
        }
    };

    const handleCustomReceive = async () => {
        setReceivingPackingSlip(true);
        try {
            const partService = ServiceLocator.Instance.getPartService();
            const userId = AuthenticationService.getInstance().getUserId();
            // Parse the receiving quantities to get actual numbers
            const parsedReceiveQuantities = Object.keys(receiveQuantities).reduce((acc, itemId) => {
                acc[itemId] = parseNumberInputValue(receiveQuantities[itemId]);
                return acc;
            }, {});
            await partService.receivePackingSlipWithQuantities(packingSlipId, parsedReceiveQuantities, userId);
            await loadData();
            setCustomReceivingMode(false);
            if (onUpdate) onUpdate();
            window.$alertSuccess('Packing slip received');
        } catch (error) {
            console.error('Error receiving packing slip with quantities:', error);
            alert('Error receiving packing slip');
        } finally {
            setReceivingPackingSlip(false);
        }
    };

    const handleReceiveQuantityChange = (itemId, quantity) => {
        setReceiveQuantities(prevState => ({
            ...prevState,
            [itemId]: quantity
        }));
    };

    const handleDeletePackingSlip = async () => {
        const confirm = window.confirm(`Are you sure you want to delete this ${packingSlip.name.indexOf(' - Box ') > -1 ? 'box' : 'packing slip'}? This action cannot be undone.`);
        if (!confirm) return;

        setDeletingPackingSlip(true);
        try {
            const partService = ServiceLocator.Instance.getPartService();
            await partService.deletePackingSlip(packingSlipId);
            if (onUpdate) onUpdate();
            onClose();
            window.$alertSuccess(`${packingSlip.name.indexOf(' - Box ') > -1 ? 'Box' : 'Packing slip'} deleted`);
        } catch (error) {
            console.error('Error deleting packing slip:', error);
            if (error.response?.status === 400 || error.response?.data?.error?.includes('Cannot delete a packing slip that has already been received')) {
                window.$alertError(`Cannot delete a ${packingSlip.name.indexOf(' - Box ') > -1 ? 'box' : 'packing slip'} that has already been received`);
            } else {
                window.$alertError(`Error deleting ${packingSlip.name.indexOf(' - Box ') > -1 ? 'box' : 'packing slip'}`);
            }
        } finally {
            setDeletingPackingSlip(false);
        }
    };

    const handleEditReceivingAction = (action) => {
        setEditingActionId(action.id);
        setEditingAction({
            quantityReceived: action.quantityReceived,
            notes: action.notes || ''
        });
    };

    const handleSaveReceivingAction = async (actionId) => {
        try {
            const partService = ServiceLocator.Instance.getPartService();
            const updatedAction = {
                ...receivingActions.find(a => a.id === actionId),
                quantityReceived: parseInt(editingAction.quantityReceived),
                notes: editingAction.notes
            };
            
            await partService.updateReceivingAction(actionId, updatedAction);
            await loadData();
            setEditingActionId(null);
            setEditingAction({});
            if (onUpdate) onUpdate();
            window.$alertSuccess('Receiving action updated');
        } catch (error) {
            console.error('Error updating receiving action:', error);
            window.$alertError('Error updating receiving action');
        }
    };

    const handleCancelEditReceivingAction = () => {
        setEditingActionId(null);
        setEditingAction({});
    };

    const handleDeleteReceivingAction = async (actionId) => {
        const confirm = window.confirm('Are you sure you want to delete this receiving action? This action cannot be undone.');
        if (!confirm) return;

        try {
            const partService = ServiceLocator.Instance.getPartService();
            await partService.deleteReceivingAction(actionId);
            await loadData();
            if (onUpdate) onUpdate();
            window.$alertSuccess('Receiving action deleted');
        } catch (error) {
            console.error('Error deleting receiving action:', error);
            window.$alertError('Error deleting receiving action');
        }
    };

    const handleEditingActionChange = (field, value) => {
        setEditingAction(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Item editing handlers
    const handleQuantityChange = (itemId, quantity) => {
        setEditedQuantities(prev => ({
            ...prev,
            [itemId]: quantity
        }));
    };

    const handleSaveAllChanges = async () => {
        setSavingChanges(true);
        try {
            const partService = ServiceLocator.Instance.getPartService();
            
            // Update all changed quantities
            const updatePromises = Object.entries(editedQuantities).map(([itemId, quantity]) => {
                return partService.updatePackingSlipItem({
                    id: itemId,
                    quantityExpected: parseNumberInputValue(quantity)
                });
            });

            // Add new item if one is being added
            if (addingNewItem && newItem.purchasedPartId && newItem.quantityExpected) {
                const itemToAdd = {
                    packingSlipId: packingSlipId,
                    purchasedPartId: newItem.purchasedPartId,
                    quantityExpected: parseNumberInputValue(newItem.quantityExpected),
                    quantityReceived: 0
                };
                updatePromises.push(partService.addItemToPackingSlip(itemToAdd));
            }

            await Promise.all(updatePromises);
            
            // Reset item editing state but keep packing slip editing active
            setEditedQuantities({});
            setAddingNewItem(false);
            setNewItem({ purchasedPartId: '', quantityExpected: 1 });
            setEditingItemMode(false);
            
            await loadData();
            window.$alertSuccess('All changes saved successfully');
        } catch (error) {
            console.error('Error saving changes:', error);
            window.$alertError('Error saving changes');
        } finally {
            setSavingChanges(false);
        }
    };

    const handleCancelAllChanges = () => {
        setEditedQuantities({});
        setAddingNewItem(false);
        setNewItem({ purchasedPartId: '', quantityExpected: 1 });
        setEditingItemMode(false);
        setEditingPackingSlip(false);
        setEditedPackingSlip({});
    };

    const handleDeleteItem = async (itemId) => {
        const confirm = window.confirm('Are you sure you want to remove this item from the packing slip?');
        if (!confirm) return;

        try {
            const partService = ServiceLocator.Instance.getPartService();
            await partService.deletePackingSlipItem(itemId);
            await loadData();
            window.$alertSuccess('Item removed from packing slip');
        } catch (error) {
            console.error('Error deleting item:', error);
            window.$alertError('Error removing item');
        }
    };



    const handleCancelAddNewItem = () => {
        setAddingNewItem(false);
        setNewItem({ purchasedPartId: '', quantityExpected: 1 });
    };

    const handleNewItemChange = (field, value) => {
        if (field === 'purchasedPartId') {
            const selectedPart = availablePurchasedParts.find(part => part.id === value);
            // Use the available quantity from the selected part
            const availableQuantity = selectedPart ? (selectedPart.quantityOrdered - selectedPart.quantityReceived) : 1;
            
            setNewItem({
                purchasedPartId: value,
                quantityExpected: Math.max(1, availableQuantity)
            });
            
            // Autofocus the quantity input when a part is selected
            if (value) {
                setTimeout(() => {
                    const quantityInput = document.querySelector('input[data-new-item-quantity]');
                    if (quantityInput) {
                        quantityInput.focus();
                        quantityInput.select();
                    }
                }, 100);
            }
        } else if (field === 'quantityExpected') {
            setNewItem(prevState => ({
                ...prevState,
                [field]: parseNumberInputValue(value)
            }));
        } else {
            setNewItem(prevState => ({
                ...prevState,
                [field]: value
            }));
        }
    };

    const handleSaveNewItem = async () => {
        if (!newItem.purchasedPartId || !newItem.quantityExpected || newItem.quantityExpected <= 0) {
            alert('Please select a part and enter a valid quantity');
            return;
        }

        // Validate that the quantity doesn't exceed available quantity
        const selectedPart = availablePurchasedParts.find(part => part.id === newItem.purchasedPartId);
        if (selectedPart) {
            const availableQuantity = selectedPart.quantityOrdered - selectedPart.quantityReceived;
            if (newItem.quantityExpected > availableQuantity) {
                alert(`Quantity cannot exceed available quantity (${availableQuantity})`);
                return;
            }
        }

        try {
            const partService = ServiceLocator.Instance.getPartService();
            const itemToAdd = {
                packingSlipId: packingSlipId,
                purchasedPartId: newItem.purchasedPartId,
                quantityExpected: parseInt(newItem.quantityExpected)
            };
            
            await partService.addItemToPackingSlip(itemToAdd);
            
            // Reset the form but keep it open for adding more items
            setNewItem({ purchasedPartId: '', quantityExpected: 1 });
            
            // Reload the data to show the new item
            await loadData();
            if (onUpdate) onUpdate();
            
            window.$alertSuccess('Item added to packing slip');
        } catch (error) {
            console.error('Error adding item to packing slip:', error);
            window.$alertError('Error adding item to packing slip');
        }
    };

    const getStatusChip = () => {
        if (!packingSlip) return null;
        
        const isFullyReceived = isPackingSlipFullyReceived(packingSlip);
        const hasAnyReceiving = packingSlip.hasAnyReceiving;
        
        if (isFullyReceived) {
            return <Chip 
                label="Fully Received" 
                style={{ 
                    backgroundColor: '#00f2ff', 
                    color: 'black',
                    fontWeight: 'bold'
                }} 
                className={classes.statusChip} 
            />;
        } else if (hasAnyReceiving) {
            return <Chip label="Partially Received" color="warning" className={classes.statusChip} />;
        } else {
            return <Chip label="Not Received" color="error" className={classes.statusChip} />;
        }
    };

    const getPartInfo = (item) => {        
        // Check if we have the purchased part data
        if (item.purchasedPart) {
            // First, try to find the purchased part in our allPurchasedParts array
            // The purchasedPartId in the item should match the id in allPurchasedParts
            const purchasedPartData = allPurchasedParts.find(pp => pp.id === item.purchasedPartId);
            if (purchasedPartData) {
                // Use the enriched data from the API
                if (purchasedPartData.partNumber && purchasedPartData.partDescription) {
                    return {
                        partId: purchasedPartData.partNumber,
                        description: purchasedPartData.partDescription
                    };
                }
            }
            
            // Fallback: check if the purchased part has direct partNumber and partDescription (from API payload)
            if (item.purchasedPart.partNumber && item.purchasedPart.partDescription) {
                return {
                    partId: item.purchasedPart.partNumber,
                    description: item.purchasedPart.partDescription
                };
            }
            
            // Look up individual part by partId
            if (item.purchasedPart.partId && parts) {
                const partData = parts.find(p => p.part.id === item.purchasedPart.partId);
                if (partData?.part) {
                    return {
                        partId: partData.part.partId,
                        description: partData.part.description
                    };
                }
            }
            
            // Look up part family by partFamilyId  
            if (item.purchasedPart.partFamilyId && partFamilies) {
                const family = partFamilies.find(f => f.id === item.purchasedPart.partFamilyId);
                if (family) {
                    return {
                        partId: family.partFamilyId,
                        description: family.description
                    };
                }
            }
            
            // Check if the purchased part has direct part description (legacy fallback)
            if (item.purchasedPart.partDescription) {
                return {
                    partId: 'PP-' + item.purchasedPart.id.substring(0, 8),
                    description: item.purchasedPart.partDescription
                };
            }
            
            // Fallback to company name if available
            if (item.purchasedPart.companyName) {
                return {
                    partId: 'VENDOR-' + item.purchasedPart.id.substring(0, 8),
                    description: item.purchasedPart.companyName
                };
            }
        }
        
        // Final fallback
        console.log('No part info found, using fallback for item:', item);
        console.log('Available purchased parts:', allPurchasedParts);
        return {
            partId: 'Unknown',
            description: 'Unknown Part'
        };
    };

    const getItemRowStyle = (item) => {
        const received = item.quantityReceived || 0;
        const expected = item.quantityExpected || 0;

        if (received >= expected && expected > 0) {
            // Fully received - cyan background with left border
            return {
                backgroundColor: '#e6f9ff',
                borderLeft: '4px solid #00f2ff'
            };
        } else if (received > 0) {
            // Partially received - orange background with left border
            return {
                backgroundColor: '#fff3e0',
                borderLeft: '4px solid #ff9800'
            };
        } else {
            // Not received - default styling
            return {
                backgroundColor: 'transparent',
                borderLeft: '4px solid transparent'
            };
        }
    };

    // Duplication handlers
    const handleDuplicatePackingSlip = () => {
        // Instead of creating duplicates in-place, redirect to the create packing slip screen
        // with pre-filled data from the current packing slip
        if (!packingSlip) return;
        
        const duplicationData = {
            action: 'duplicate',
            packingSlip: {
                name: `${packingSlip.name}`,
                vendorName: packingSlip.vendorName,
                trackingNumber: packingSlip.trackingNumber || '',
                fileUrl: packingSlip.fileUrl || '',
                fileName: packingSlip.fileName || '',
                items: packingSlip.items?.map(item => ({
                    purchasedPartId: item.purchasedPartId,
                    quantityExpected: Number(item.quantityExpected) || 0,
                    purchasedPart: item.purchasedPart
                })) || []
            }
        };
        
        if (asPage && onRequestDuplicate) {
            onRequestDuplicate(duplicationData);
            return;
        }
        onClose();
        window.dispatchEvent(new CustomEvent('duplicatePackingSlip', { detail: duplicationData }));
    };

    // Edit packing slip handlers
    const handleEditPackingSlip = () => {
        setEditedPackingSlip({
            name: packingSlip.name,
            trackingNumber: packingSlip.trackingNumber || '',
            vendorName: packingSlip.vendorName
        });
        setEditingPackingSlip(true);
    };

    const handleCancelEditPackingSlip = () => {
        setEditingPackingSlip(false);
        setEditedPackingSlip({});
        setEditingItemMode(false);
        setEditedQuantities({});
        setAddingNewItem(false);
        setNewItem({ purchasedPartId: '', quantityExpected: 1 });
    };

    const handleSaveEditPackingSlip = async () => {
        if (!editedPackingSlip.name.trim()) {
            alert('Please enter a packing slip name');
            return;
        }

        try {
            const partService = ServiceLocator.Instance.getPartService();
            
            // Update packing slip details
            const updatedPackingSlip = {
                id: packingSlip.id,
                name: editedPackingSlip.name,
                trackingNumber: editedPackingSlip.trackingNumber || null,
                vendorName: editedPackingSlip.vendorName
            };

            await partService.updatePackingSlip(updatedPackingSlip);
            
            setEditingPackingSlip(false);
            setEditedPackingSlip({});
            
            // Reload the data
            await loadData();
            if (onUpdate) onUpdate();
            
            window.$alertSuccess('Packing slip updated successfully');
        } catch (error) {
            console.error('Error updating packing slip:', error);
            window.$alertError('Error updating packing slip');
        }
    };

    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setUploadingFile(true);
        try {
            const partService = ServiceLocator.Instance.getPartService();
            await partService.uploadPackingSlipFile(packingSlip.id, file);
            
            // Reload the data
            await loadData();
            if (onUpdate) onUpdate();
            
            window.$alertSuccess('File uploaded successfully');
        } catch (error) {
            console.error('Error uploading file:', error);
            window.$alertError('Error uploading file');
        } finally {
            setUploadingFile(false);
        }
    };

    const packingSlipFileUrl = packingSlip?.fileUrl || packingSlip?.FileUrl;
    const packingSlipFileName = packingSlip?.fileName || packingSlip?.FileName;
    const multiBoxInfo = packingSlip ? parseMultiBoxName(packingSlip.name) : null;

    const handleDownloadFile = () => {
        if (!packingSlipFileUrl) return;

        const downloadFileName = packingSlipFileName || packingSlipFileUrl.split('/').pop() || 'packing-slip-file';
        const xhr = new XMLHttpRequest();
        xhr.responseType = 'blob';

        xhr.onload = function () {
            const hyperlinkElement = document.createElement("a");
            hyperlinkElement.href = URL.createObjectURL(xhr.response);
            hyperlinkElement.setAttribute("download", downloadFileName);
            hyperlinkElement.click();
        };

        xhr.open('GET', packingSlipFileUrl);
        xhr.send();
    };

    const hasUnsavedChanges = (editingItemMode || addingNewItem || Object.keys(editedQuantities).length > 0) || editingPackingSlip;

    const closeDetails = () => {
        setEditingPackingSlip(false);
        setEditingItemMode(false);
        setAddingNewItem(false);
        setEditedQuantities({});
        setEditedPackingSlip({});
        setNewItem({ purchasedPartId: '', quantityExpected: 1 });
        onClose();
    };

    return (
        <DetailsShell
            asPage={asPage}
            open={open}
            onClose={closeDetails}
            title={`Packing Slip Details - ${packingSlip?.name || 'Loading...'}`}
            hasUnsavedChanges={hasUnsavedChanges}
            onSubmit={handleSaveAllChanges}
            submitConfirmMessage="Are you sure you want to save all changes to this packing slip?"
        >
                {loading ? (
                    <Box className={classes.loadingContainer}>
                        <CircularProgress />
                    </Box>
                ) : packingSlip ? (
                    <>
                        {/* Header Row - Vendor Info, Status Chip, and Spacer */}
                        <div className={classes.headerRow}>
                            <div className={classes.vendorInfo}>
                                <Typography variant="h6">Vendor: {packingSlip.vendorName}</Typography>
                                {multiBoxInfo && (
                                    <Typography variant="h6">Box #: {multiBoxInfo.boxNumber}</Typography>
                                )}
                                <Typography variant="body2">Created: {formatUtcForDisplay(packingSlip.dateCreated)}</Typography>
                                {packingSlip.trackingNumber && (
                                    <Typography variant="body2">Tracking: {packingSlip.trackingNumber}</Typography>
                                )}
                                <Typography variant="body2" component="div">
                                    File:{' '}
                                    {packingSlipFileUrl ? (
                                        <span
                                            className="packing-slip-file"
                                            onClick={handleDownloadFile}
                                            title="Download packing slip file"
                                            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, verticalAlign: 'middle' }}
                                        >
                                            <GetAppIcon style={{ fontSize: 16 }} />
                                            {packingSlipFileName || packingSlipFileUrl.split('/').pop() || 'Download'}
                                        </span>
                                    ) : (
                                        <span style={{ color: '#888' }}>None</span>
                                    )}
                                </Typography>
                            </div>
                            <div className={classes.statusContainer}>
                                {getStatusChip()}
                            </div>
                            <div className={classes.spacer}></div>
                        </div>

                        {/* Action Buttons */}
                        <div className={classes.actionButtons}>
                            {/* Left side - Receiving buttons */}
                            <div className={classes.leftButtons}>
                                {!isPackingSlipFullyReceived(packingSlip) && (
                                    <>
                                        <Button 
                                            variant="contained"
                                            color="primary"
                                            onClick={handleReceiveAll}
                                            disabled={receivingPackingSlip}
                                            className="success-button-lg"
                                        >
                                            Receive All
                                        </Button>
                                        <Button
                                            color="primary"
                                            onClick={() => {
                                                const newMode = !customReceivingMode;
                                                setCustomReceivingMode(newMode);
                                                
                                                // Initialize receive quantities with outstanding amounts when enabling custom receive mode
                                                if (newMode && packingSlip?.items) {
                                                    const defaultQuantities = {};
                                                    packingSlip.items.forEach(item => {
                                                        const outstanding = Math.max(0, item.quantityExpected - item.quantityReceived);
                                                        defaultQuantities[item.id] = outstanding;
                                                    });
                                                    setReceiveQuantities(defaultQuantities);
                                                } else if (!newMode) {
                                                    // Clear quantities when disabling custom receive mode
                                                    setReceiveQuantities({});
                                                }
                                            }}
                                            className={customReceivingMode ? "cancel-button-lg" : "success-button-lg"}
                                        >
                                            {customReceivingMode ? 'Cancel Custom Receive' : 'Custom Receive'}
                                        </Button>
                                    </>
                                )}
                                
                                {/* Custom Receive Submit Button */}
                                {customReceivingMode && !isPackingSlipFullyReceived(packingSlip) && (
                                    receivingPackingSlip ? (
                                        <CircularProgress size={24} />
                                    ) : (
                                        <Button 
                                            variant="contained"
                                            color="primary"
                                            onClick={handleCustomReceive}
                                            className="success-button-lg"
                                        >
                                            Submit Custom Receive
                                        </Button>
                                    )
                                )}
                            </div>
                            
                                                         {/* Right side - Other buttons */}
                             <div className={classes.rightButtons}>
                                 {!editingItemMode && !editingPackingSlip ? (
                                     <>
                                         <Button
                                             color="primary"
                                             onClick={(event) => {
                                                 if (asPage && onRequestEdit) {
                                                     onRequestEdit(packingSlip, event);
                                                     return;
                                                 }
                                                 window.dispatchEvent(new CustomEvent('editPackingSlip', {
                                                     detail: { packingSlip }
                                                 }));
                                                 onClose();
                                             }}
                                             disabled={receivingPackingSlip}
                                             className="success-button-lg"
                                         >
                                             Edit {packingSlip.name.indexOf(' - Box ') > -1 ? 'Box' : 'Packing Slip'}
                                         </Button>
                                     </>
                                 ) : (
                                     <>
                                         <Button 
                                             variant="contained"
                                             color="primary"
                                             onClick={async () => {
                                                 setSavingChanges(true);
                                                 try {
                                                     // Save item changes first
                                                     if (editingItemMode || addingNewItem || Object.keys(editedQuantities).length > 0) {
                                                         await handleSaveAllChanges();
                                                     }
                                                     
                                                     // Then save packing slip changes
                                                     if (editingPackingSlip) {
                                                         await handleSaveEditPackingSlip();
                                                     }
                                                     
                                                     window.$alertSuccess('All changes saved successfully');
                                                 } catch (error) {
                                                     console.error('Error saving changes:', error);
                                                     window.$alertError('Error saving changes');
                                                 } finally {
                                                     setSavingChanges(false);
                                                     setEditingPackingSlip(false);
                                                     setEditingItemMode(false);
                                                     setAddingNewItem(false);
                                                     setEditedQuantities({});
                                                     setEditedPackingSlip({});
                                                     setNewItem({ purchasedPartId: '', quantityExpected: 1 });
                                                 }
                                             }}
                                             disabled={savingChanges}
                                             className="success-button-lg"
                                         >
                                             {savingChanges ? 'Saving...' : 'Save All Changes'}
                                         </Button>
                                         <Button
                                             color="primary"
                                             onClick={() => {
                                                 handleCancelAllChanges();
                                                 handleCancelEditPackingSlip();
                                             }}
                                             disabled={savingChanges}
                                             className="cancel-button-lg"
                                         >
                                             Cancel
                                         </Button>
                                     </>
                                 )}
                                <Button 
                                    variant="contained"
                                    color="secondary"
                                    onClick={handleDeletePackingSlip}
                                    disabled={deletingPackingSlip || receivingPackingSlip}
                                    className="delete-button-lg"
                                >
                                    {deletingPackingSlip ? "Deleting..." : packingSlip.name.indexOf(' - Box ') > -1 ? 'Delete Box' : 'Delete Packing Slip'}
                                </Button>
                            </div>
                        </div>

                        {/* Edit Packing Slip Form */}
                        {editingPackingSlip && (
                            <div style={{ marginBottom: 20, padding: 16, border: '1px solid #ddd', borderRadius: 4 }}>
                                <Typography variant="h6" style={{ marginBottom: 16 }}>
                                    Edit Packing Slip Details
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            label="Packing Slip Name"
                                            value={editedPackingSlip.name || ''}
                                            onChange={(e) => setEditedPackingSlip(prev => ({ ...prev, name: e.target.value }))}
                                            variant="outlined"
                                            fullWidth
                                            margin="normal"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            label="Tracking Number"
                                            value={editedPackingSlip.trackingNumber || ''}
                                            onChange={(e) => setEditedPackingSlip(prev => ({ ...prev, trackingNumber: e.target.value }))}
                                            variant="outlined"
                                            fullWidth
                                            margin="normal"
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle1" style={{ marginBottom: 8 }}>
                                            File Management
                                        </Typography>
                                        {packingSlipFileUrl ? (
                                            <div style={{ marginBottom: 8 }}>
                                                <Typography variant="body2" color="textSecondary">
                                                    Current file: {packingSlipFileName || 'packing-slip-file'}
                                                </Typography>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    onClick={handleDownloadFile}
                                                    style={{ marginRight: 8 }}
                                                >
                                                    Download Current File
                                                </Button>
                                            </div>
                                        ) : (
                                            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
                                                No file currently attached
                                            </Typography>
                                        )}
                                        <input
                                            type="file"
                                            onChange={handleFileUpload}
                                            disabled={uploadingFile}
                                            style={{ marginBottom: 8 }}
                                        />
                                        {uploadingFile && (
                                            <CircularProgress size={20} style={{ marginLeft: 8 }} />
                                        )}
                                    </Grid>
                                </Grid>
                            </div>
                        )}

                        {/* Packing Slip Items */}
                        <div className={classes.itemsTable}>
                            <div className="parts-table-container">
                                <table className="parts-table">
                                    <thead>
                                        <tr>
                                            <th style={{minWidth: 120}}>Part Number</th>
                                            <th style={{minWidth: 100}}>Expected</th>
                                            <th style={{minWidth: 100}}>Received</th>
                                            {customReceivingMode && <th style={{minWidth: 100}}>Receive Qty</th>}
                                            {editingItemMode && <th style={{minWidth: 120}}>Actions</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {/* Existing Items */}
                                        {packingSlip.items?.sort((a, b) => {
                                            const partInfoA = getPartInfo(a);
                                            const partInfoB = getPartInfo(b);
                                            return partInfoA.partId.localeCompare(partInfoB.partId);
                                        }).map((item) => {
                                            const partInfo = getPartInfo(item);
                                            const outstandingQty = Math.max(0, item.quantityExpected - item.quantityReceived);
                                            const currentQuantity = editedQuantities[item.id] !== undefined ? editedQuantities[item.id] : item.quantityExpected;
                                            
                                            return (
                                                <tr key={item.id} style={getItemRowStyle(item)}>
                                                    <td>
                                                        <PartDisplay 
                                                            part={{
                                                                partId: partInfo.partId,
                                                                revision: partInfo.revision || '',
                                                                description: partInfo.description
                                                            }}
                                                            variant="table"
                                                            classes={classes}
                                                        />
                                                    </td>
                                                    <td>
                                                        {editingItemMode ? (
                                                            <TextField
                                                                type="number"
                                                                value={currentQuantity || ''}
                                                                onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                                                                variant="outlined"
                                                                size="small"
                                                                style={{width: 80}}
                                                                inputProps={{ min: 1 }}
                                                            />
                                                        ) : (
                                                            item.quantityExpected
                                                        )}
                                                    </td>
                                                    <td>{item.quantityReceived}</td>
                                                    {customReceivingMode && (
                                                        <td>
                                                            <TextField
                                                                type="number"
                                                                value={receiveQuantities[item.id] || ''}
                                                                onChange={(e) => handleReceiveQuantityChange(item.id, e.target.value)}
                                                                variant="outlined"
                                                                size="small"
                                                                style={{width: 80}}
                                                                inputProps={{ min: 0, max: outstandingQty }}
                                                            />
                                                        </td>
                                                    )}
                                                    {editingItemMode && (
                                                        <td>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleDeleteItem(item.id)}
                                                                color="error"
                                                                title="Delete item"
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </td>
                                                    )}
                                                </tr>
                                            );
                                        })}

                                        {/* Add New Item Form - shows when adding */}
                                        {editingItemMode && addingNewItem && (
                                            <tr style={{backgroundColor: '#f0f8ff'}}>
                                                <td>
                                                    <select
                                                        value={newItem.purchasedPartId}
                                                        onChange={(e) => handleNewItemChange('purchasedPartId', e.target.value)}
                                                        style={{width: '100%', height: '32px'}}
                                                    >
                                                        <option value="">Select Part...</option>
                                                        {availablePurchasedParts.map((part) => (
                                                            <option key={part.id} value={part.id}>
                                                                {part.partNumber || part.partFamilyName} (PO #{part.purchaseOrderNumber} - {formatUtcForDisplay(part.orderDate)})
                                                            </option>
                                                        ))}
                                                    </select>
                                                </td>
                                                <td className="description-ellipsis text-left">
                                                    {newItem.purchasedPartId && (
                                                        availablePurchasedParts.find(p => p.id === newItem.purchasedPartId)?.partDescription ||
                                                        availablePurchasedParts.find(p => p.id === newItem.purchasedPartId)?.partFamilyName ||
                                                        'Description'
                                                    )}
                                                </td>
                                                <td>
                                                    <TextField
                                                        type="number"
                                                        value={newItem.quantityExpected || ''}
                                                        onChange={(e) => handleNewItemChange('quantityExpected', e.target.value)}
                                                        variant="outlined"
                                                        size="small"
                                                        style={{width: 80}}
                                                        inputProps={{ 
                                                            min: 1,
                                                            'data-new-item-quantity': true
                                                        }}
                                                    />
                                                </td>
                                                <td>0</td>
                                                {customReceivingMode && <td>-</td>}
                                                <td>
                                                    <div>
                                                        <IconButton
                                                            size="small"
                                                            onClick={handleSaveNewItem}
                                                            color="primary"
                                                            title="Save item"
                                                        >
                                                            <CheckIcon fontSize="small" />
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            onClick={handleCancelAddNewItem}
                                                            color="secondary"
                                                            title="Cancel"
                                                        >
                                                            <CloseIcon fontSize="small" />
                                                        </IconButton>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}

                                        {/* Add Item Button - visible when not adding an item */}
                                        {editingItemMode && !addingNewItem && (
                                            <tr>
                                                <td colSpan={customReceivingMode ? 6 : 5} style={{textAlign: 'center', padding: '10px'}}>
                                                    <Button
                                                        color="primary"
                                                        onClick={() => setAddingNewItem(true)}
                                                        className="success-button-lg"
                                                    >
                                                        Add Item
                                                    </Button>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>



                        {/* Receiving Actions Audit Trail */}
                        <Divider />
                        <div className={classes.receivingActionsTable}>
                            <Typography variant="h6" className={classes.sectionTitle}>
                                Receiving Actions History
                            </Typography>
                            
                            {receivingActions.length === 0 ? (
                                <Typography variant="body2" color="textSecondary" style={{textAlign: 'center', padding: 20}}>
                                    No receiving actions recorded yet
                                </Typography>
                            ) : (
                                <div className="parts-table-container">
                                    <table className="parts-table">
                                        <thead>
                                            <tr>
                                                <SortableTableHeader field="receivedDate" sortField={receivingActionsSortField} sortDirection={receivingActionsSortDirection} onSort={handleReceivingActionsSort} style={{minWidth: 170}}>Date</SortableTableHeader>
                                                <SortableTableHeader field="partNumber" sortField={receivingActionsSortField} sortDirection={receivingActionsSortDirection} onSort={handleReceivingActionsSort}>Part Number</SortableTableHeader>
                                                <SortableTableHeader field="quantityReceived" sortField={receivingActionsSortField} sortDirection={receivingActionsSortDirection} onSort={handleReceivingActionsSort} style={{minWidth: 60}}>Quantity</SortableTableHeader>
                                                <SortableTableHeader field="userName" sortField={receivingActionsSortField} sortDirection={receivingActionsSortDirection} onSort={handleReceivingActionsSort} style={{minWidth: 130}}>User</SortableTableHeader>
                                                <SortableTableHeader field="notes" sortField={receivingActionsSortField} sortDirection={receivingActionsSortDirection} onSort={handleReceivingActionsSort} style={{minWidth: 110}}>Notes</SortableTableHeader>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sortedReceivingActions.map((action) => {
                                                const isEditing = editingActionId === action.id;
                                                return (
                                                    <tr key={action.id}>
                                                        <td>{formatUtcForDisplay(action.receivedDate)}</td>
                                                        <td>
                                                            <PartDisplay 
                                                                part={{
                                                                    partId: action.partNumber,
                                                                    revision: action.partRevision || '',
                                                                    description: action.partDescription
                                                                }}
                                                                variant="table"
                                                                classes={classes}
                                                            />
                                                        </td>
                                                        <td>
                                                            {isEditing ? (
                                                                <TextField
                                                                    type="number"
                                                                    value={editingAction.quantityReceived || ''}
                                                                    onChange={(e) => handleEditingActionChange('quantityReceived', e.target.value)}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    style={{width: 80}}
                                                                    inputProps={{ min: 1 }}
                                                                />
                                                            ) : (
                                                                action.quantityReceived
                                                            )}
                                                        </td>
                                                        <td>{action.userName}</td>
                                                        <td>
                                                            {isEditing ? (
                                                                <TextField
                                                                    value={editingAction.notes || ''}
                                                                    onChange={(e) => handleEditingActionChange('notes', e.target.value)}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    style={{minWidth: 150}}
                                                                    placeholder="Notes"
                                                                />
                                                            ) : (
                                                                action.notes || '-'
                                                            )}
                                                        </td>
                                                        <td>
                                                            {isEditing ? (
                                                                <div style={{display: 'flex', gap: '5px'}}>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => handleSaveReceivingAction(action.id)}
                                                                        color="primary"
                                                                    >
                                                                        <CheckIcon fontSize="small" />
                                                                    </IconButton>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={handleCancelEditReceivingAction}
                                                                        color="secondary"
                                                                    >
                                                                        <CloseIcon fontSize="small" />
                                                                    </IconButton>
                                                                </div>
                                                            ) : (
                                                                <div style={{display: 'flex', gap: '5px'}}>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => handleEditReceivingAction(action)}
                                                                        color="primary"
                                                                    >
                                                                        <EditIcon fontSize="small" />
                                                                    </IconButton>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => handleDeleteReceivingAction(action.id)}
                                                                        color="error"
                                                                    >
                                                                        <DeleteIcon fontSize="small" />
                                                                    </IconButton>
                                                                </div>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>


                    </>
                ) : (
                    <Typography>No packing slip data available</Typography>
                )}
        </DetailsShell>
    );
};

PackingSlipDetails.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    packingSlipId: PropTypes.string,
    onUpdate: PropTypes.func,
    parts: PropTypes.array,
    partFamilies: PropTypes.array,
    packingSlips: PropTypes.array,
    classes: PropTypes.object,
    asPage: PropTypes.bool,
    onRequestEdit: PropTypes.func,
    onRequestDuplicate: PropTypes.func,
};

export default withStyles(styles)(PackingSlipDetails); 